import { FormatType } from '../../types';
import { getFormatter, withExplicitSign } from '../../utilities';
import { PlotTooltip, type TooltipPosition } from '../plot-tooltip';
import styles from './chord-chart.module.css';
import type { ChordFlowMode } from './types';

type ChordGroupTooltipProps = {
  label: string;
  inflow: number;
  outflow: number;
  isOpen: boolean;
  cursor: TooltipPosition | null;
  flowMode: ChordFlowMode;
};

export const ChordGroupTooltip = ({
  label,
  inflow,
  outflow,
  isOpen,
  cursor,
  flowMode,
}: ChordGroupTooltipProps) => {
  if (!isOpen || cursor === null) return null;
  return (
    <PlotTooltip
      isVisible
      x={cursor.x}
      y={cursor.y}
      placement="vertical"
      space="viewport"
    >
      <TooltipContent
        label={label}
        inflow={inflow}
        outflow={outflow}
        flowMode={flowMode}
      />
    </PlotTooltip>
  );
};

const format = getFormatter(FormatType.INTEGER);
const formatSigned = withExplicitSign(format);

const TooltipContent = ({
  label,
  inflow,
  outflow,
  flowMode,
}: {
  label: string;
  inflow: number;
  outflow: number;
  flowMode: ChordFlowMode;
}) => {
  const isNet = flowMode === 'net';
  const inflowLabel = isNet ? 'Net Inflow' : 'Inflow';
  const outflowLabel = isNet ? 'Net Outflow' : 'Outflow';
  // Net flow collapses each pair to a single direction, so every node ends up
  // with either inflow or outflow at zero. Hide the zero side to avoid noise.
  const showInflow = !isNet || inflow > 0;
  const showOutflow = !isNet || outflow > 0;
  // In directional mode both sides are shown, so surface the net (inflow −
  // outflow) here instead of as a separate chart mode.
  const netFlow = inflow - outflow;
  return (
    <div className={styles.tooltipBlock}>
      <div className={styles.tooltipTitle}>{label}</div>
      <dl className={styles.tooltipMetrics}>
        {showInflow && (
          <>
            <dt>{inflowLabel}</dt>
            <dd>{format(inflow)}</dd>
          </>
        )}
        {showOutflow && (
          <>
            <dt>{outflowLabel}</dt>
            <dd>{format(outflow)}</dd>
          </>
        )}
        {!isNet && (
          <>
            <dt>Net flow</dt>
            <dd>{formatSigned(netFlow)}</dd>
          </>
        )}
      </dl>
    </div>
  );
};
